import type { CustomizerReducerActiveModeType } from '@europrocurement/l2d-domain';
import type { Theme } from '@mui/system';

import _ from 'lodash';
import {
    BLUE_THEME,
    GREEN_THEME,
    RED_THEME,
    PINK_THEME,
    BLACK_THEME,
    PURPLE_THEME,
    INDIGO_THEME,
    ORANGE_THEME,
    L2D_THEME,
    THEME_LIST,
} from '@europrocurement/l2d-domain';

import { createTheme } from '@mui/material/styles';
import { ThemeOptions } from '@mui/material/styles/createTheme';
import { useSelector } from 'react-redux';
import { FlexyLayoutState } from '../../redux/storeConfig/store';
import typography from './Typography';
import components from './Override';
import shadows from './Shadows';
import { buildComponentTokens } from './componentTokens';

declare module '@mui/material/styles/createPalette' {
    interface TypeBackground {
        default: string;
        dark: string;
        paper: string;
        shadow: string;
    }

    interface TypeText {
        light: string;
        gris70: string;
        gris50: string;
        gris35: string;
        gris10: string;
        gris005: string;
    }

    interface Palette {
        cardboard: PaletteColor;
        prescriberStatus: {
            regular: string;
            dropout: string;
            notInterested: string;
        };
    }

    interface PaletteOptions {
        danger?: PaletteColor;
        cardboard?: PaletteColorOptions;
        prescriberStatus?: {
            regular: string;
            dropout: string;
            notInterested: string;
        };
    }
}

declare module '@mui/material/Chip' {
    interface ChipPropsColorOverrides {
        danger: true;
    }
}

// ##############################
// Design System Constants - Figma Official Colors
// ##############################

// Couleurs Primary & Secondary (Light Mode)
const L2D_PRIMARY = '#177BC2';
const L2D_PRIMARY_LIGHT = '#00A8E4';
const L2D_SECONDARY = '#0A1A53';

// Couleurs Primary & Secondary (Dark Mode)
const L2D_PRIMARY_DARK = '#00A8E4';
const L2D_PRIMARY_DARK_LIGHT = '#1AC3FF';

// Couleurs Status (communes Light/Dark)
const L2D_SUCCESS = '#00B884';
const L2D_WARNING = '#FF8229';
const L2D_ERROR = '#ED392C';

// Couleurs Complémentaires (codes erreur, accents)
const L2D_COMPLEMENTARY = {
    PURPLE: '#7A45E2',
    ORANGE: '#EC835D',
    OLIVE: '#A1BE4D',
    RED: '#D85945',
    ROSE: '#E5316E',
    AMBER: '#FFA000',
    BROWN: '#795548',
    TEAL: '#27B1A4',
    BLUE: '#2962FF',
};

// Couleurs Carton (illustration B2D 404)
const CARDBOARD = {
    MAIN: '#decc78',
    LIGHT: '#f6e387',
    DARK: '#bfaf67',
};

// Couleurs Statuts Prescripteur (CustomerServiceWidget)
const PRESCRIBER_STATUS_COLORS = {
    REGULAR: '#003dcc',
    DROPOUT: '#7b5cda',
    NOT_INTERESTED: '#ea2bff',
};

// Couleurs Texte
const TEXT_LIGHT = {
    PRIMARY: '#0A1A53',
    SECONDARY_70: 'rgba(10, 26, 83, 0.70)',
    SECONDARY_50: 'rgba(10, 26, 83, 0.50)',
    SECONDARY_35: 'rgba(10, 26, 83, 0.35)',
    SECONDARY_10: 'rgba(10, 26, 83, 0.10)',
    SECONDARY_05: 'rgba(10, 26, 83, 0.05)',
};

const TEXT_DARK = {
    PRIMARY: '#FFFFFF',
    SECONDARY_70: 'rgba(255, 255, 255, 0.70)',
    SECONDARY_50: 'rgba(255, 255, 255, 0.50)',
    SECONDARY_35: 'rgba(255, 255, 255, 0.35)',
    SECONDARY_10: 'rgba(255, 255, 255, 0.10)',
    SECONDARY_05: 'rgba(255, 255, 255, 0.05)',
};

// Couleurs Fond
const BACKGROUND_LIGHT = {
    DEFAULT: '#F6F7FA',
    PAPER: '#FFFFFF',
    SHADOW: 'rgba(0, 0, 0, 0.08)',
};

const BACKGROUND_DARK = {
    DEFAULT: '#0C0D0D',
    PAPER: '#292C32',
    SHADOW: 'rgba(0, 0, 0, 0.5)',
};

// ##############################
// Contrast & Tonal Settings
// ##############################

// Seuil de contraste utilisé par palette.getContrastText() pour choisir entre
// texte blanc ou noir sur un fond coloré. WCAG AA recommande 4.5, MUI défaut = 3.
const contrastThreshold = 3;

// Décalage de luminosité appliqué pour générer automatiquement les variantes
// light/dark d'une couleur si elles ne sont pas explicitement définies.
// MUI défaut = 0.2 (20% de décalage)
const tonalOffset = 0.2;

// ##############################
// Spacing System (8px grid)
// ##############################
const spacing = {
    xs: 8, // 8px
    sm: 16, // 16px
    md: 24, // 24px
    lg: 32, // 32px
    xl: 40, // 40px
    xxl: 56, // 56px
    xxxl: 72, // 72px
};

// ##############################
// Theme Builders
// ##############################

const buildLightPalette = () => ({
    primary: {
        main: L2D_PRIMARY,
        light: L2D_PRIMARY_LIGHT,
        dark: '#155A94',
        contrastText: '#FFFFFF',
    },
    secondary: {
        main: L2D_SECONDARY,
        light: '#5C82FF',
        dark: '#1D44B2',
        contrastText: '#FFFFFF',
    },
    success: {
        main: L2D_SUCCESS,
        light: '#33CC9E',
        dark: '#008C63',
        contrastText: '#FFFFFF',
    },
    warning: {
        main: L2D_WARNING,
        light: '#FFA054',
        dark: '#CC6A1F',
        contrastText: '#FFFFFF',
    },
    error: {
        main: L2D_ERROR,
        light: '#F2635D',
        dark: '#BC2B23',
        contrastText: '#FFFFFF',
    },
    info: {
        main: L2D_PRIMARY_LIGHT,
        light: '#33CCF2',
        dark: '#008AB8',
        contrastText: '#FFFFFF',
    },
    danger: {
        main: L2D_COMPLEMENTARY.ORANGE,
        light: '#F0A084',
        dark: '#B86845',
        contrastText: '#FFFFFF',
    },
    grey: {
        50: '#FAFAFA',
        100: '#F5F5F5',
        200: '#EEEEEE',
        300: '#E0E0E0',
        400: '#BDBDBD',
        500: '#9E9E9E',
        600: '#757575',
        700: '#616161',
        800: '#424242',
        900: '#212121',
        A100: '#ECECF2',
        A200: '#99ABB4',
        A400: '#767E89',
        A700: '#E6F4FF',
    },
    text: {
        primary: TEXT_LIGHT.PRIMARY,
        secondary: TEXT_LIGHT.SECONDARY_70,
        disabled: TEXT_LIGHT.SECONDARY_50,
        light: TEXT_LIGHT.SECONDARY_35,
        // Custom tokens for consistent styling
        gris70: TEXT_LIGHT.SECONDARY_70,
        gris50: TEXT_LIGHT.SECONDARY_50,
        gris35: TEXT_LIGHT.SECONDARY_35,
        gris10: TEXT_LIGHT.SECONDARY_10,
        gris005: TEXT_LIGHT.SECONDARY_05,
    },
    background: {
        default: BACKGROUND_LIGHT.DEFAULT,
        paper: BACKGROUND_LIGHT.PAPER,
        shadow: BACKGROUND_LIGHT.SHADOW,
    },
    action: {
        disabledBackground: 'rgba(73, 82, 88, 0.12)',
        hoverOpacity: 0.04,
        hover: TEXT_LIGHT.SECONDARY_05,
    },
    divider: TEXT_LIGHT.SECONDARY_10,
    // Couleurs personnalisées pour le design system
    ...L2D_COMPLEMENTARY,
    cardboard: {
        main: CARDBOARD.MAIN,
        light: CARDBOARD.LIGHT,
        dark: CARDBOARD.DARK,
        contrastText: '#000000',
    },
    prescriberStatus: {
        regular: PRESCRIBER_STATUS_COLORS.REGULAR,
        dropout: PRESCRIBER_STATUS_COLORS.DROPOUT,
        notInterested: PRESCRIBER_STATUS_COLORS.NOT_INTERESTED,
    },
});

const buildDarkPalette = () => ({
    primary: {
        main: L2D_PRIMARY_DARK,
        light: L2D_PRIMARY_DARK_LIGHT,
        dark: '#007AAD',
        contrastText: '#FFFFFF',
    },
    secondary: {
        main: L2D_SECONDARY,
        light: '#5C82FF',
        dark: '#1D44B2',
        contrastText: '#FFFFFF',
    },
    success: {
        main: L2D_SUCCESS,
        light: '#33CC9E',
        dark: '#008C63',
        contrastText: '#FFFFFF',
    },
    warning: {
        main: L2D_WARNING,
        light: '#FFA054',
        dark: '#CC6A1F',
        contrastText: '#FFFFFF',
    },
    error: {
        main: L2D_ERROR,
        light: '#F2635D',
        dark: '#BC2B23',
        contrastText: '#FFFFFF',
    },
    info: {
        main: L2D_PRIMARY_DARK,
        light: L2D_PRIMARY_DARK_LIGHT,
        dark: '#007AAD',
        contrastText: '#FFFFFF',
    },
    danger: {
        main: L2D_COMPLEMENTARY.ORANGE,
        light: '#F0A084',
        dark: '#B86845',
        contrastText: '#FFFFFF',
    },
    grey: {
        50: '#FAFAFA',
        100: '#F5F5F5',
        200: '#EEEEEE',
        300: '#E0E0E0',
        400: '#BDBDBD',
        500: '#9E9E9E',
        600: '#757575',
        700: '#616161',
        800: '#424242',
        900: '#212121',
        A100: '#2D333B',
        A200: '#3D4450',
        A400: '#4D5560',
        A700: '#5D6570',
    },
    text: {
        primary: TEXT_DARK.PRIMARY,
        secondary: TEXT_DARK.SECONDARY_70,
        disabled: TEXT_DARK.SECONDARY_50,
        light: TEXT_DARK.SECONDARY_35,
        // Custom tokens for consistent styling
        gris70: TEXT_DARK.SECONDARY_70,
        gris50: TEXT_DARK.SECONDARY_50,
        gris35: TEXT_DARK.SECONDARY_35,
        gris10: TEXT_DARK.SECONDARY_10,
        gris005: TEXT_DARK.SECONDARY_05,
    },
    background: {
        default: BACKGROUND_DARK.DEFAULT,
        paper: BACKGROUND_DARK.PAPER,
        shadow: BACKGROUND_DARK.SHADOW,
    },
    action: {
        disabledBackground: 'rgba(255, 255, 255, 0.12)',
        hoverOpacity: 0.08,
        hover: TEXT_DARK.SECONDARY_05,
    },
    divider: TEXT_DARK.SECONDARY_10,
    ...L2D_COMPLEMENTARY,
    cardboard: {
        main: CARDBOARD.MAIN,
        light: CARDBOARD.LIGHT,
        dark: CARDBOARD.DARK,
        contrastText: '#000000',
    },
    prescriberStatus: {
        regular: PRESCRIBER_STATUS_COLORS.REGULAR,
        dropout: PRESCRIBER_STATUS_COLORS.DROPOUT,
        notInterested: PRESCRIBER_STATUS_COLORS.NOT_INTERESTED,
    },
});

// Tokens de base dark mode : backgrounds, textes, actions — communs à tous les thèmes.
// À merger par-dessus les couleurs primaires/secondaires propres à chaque thème.
const buildDarkBase = () => ({
    mode: 'dark',
    background: {
        default: BACKGROUND_DARK.DEFAULT,
        paper: BACKGROUND_DARK.PAPER,
        shadow: BACKGROUND_DARK.SHADOW,
    },
    text: {
        primary: TEXT_DARK.PRIMARY,
        secondary: TEXT_DARK.SECONDARY_70,
        disabled: TEXT_DARK.SECONDARY_50,
        light: TEXT_DARK.SECONDARY_35,
        gris70: TEXT_DARK.SECONDARY_70,
        gris50: TEXT_DARK.SECONDARY_50,
        gris35: TEXT_DARK.SECONDARY_35,
        gris10: TEXT_DARK.SECONDARY_10,
        gris005: TEXT_DARK.SECONDARY_05,
    },
    action: {
        disabledBackground: 'rgba(255, 255, 255, 0.12)',
        hoverOpacity: 0.08,
        hover: TEXT_DARK.SECONDARY_05,
    },
    divider: TEXT_DARK.SECONDARY_10,
});

// Ancien système de couleurs (legacy - à supprimer progressivement)
export const buildLegacyPalette = () => ({
    primary: {
        main: '#1a97f5',
        light: '#e6f4ff',
        dark: '#1682d4',
        contrastText: '#ffffff',
    },
    secondary: {
        main: '#1e4db7',
        light: '#ddebff',
        dark: '#173f98',
        contrastText: '#ffffff',
    },
    success: {
        main: '#00c292',
        light: '#ebfaf2',
        dark: '#00964b',
        contrastText: '#ffffff',
    },
    info: {
        main: '#0bb2fb',
        light: '#a7e3f4',
    },
    error: {
        main: '#e46a76',
        light: '#fdf3f5',
        dark: '#e45a68',
    },
    danger: {
        main: '#FF8552',
        contrastText: '#ffffff',
        light: '#ffb680',
        dark: '#c75626',
    },
    warning: {
        main: '#fec90f',
        light: '#fff4e5',
        dark: '#dcb014',
        contrastText: '#ffffff',
    },
});
export type ColorsPaletteVariant =
    | 'primary'
    | 'secondary'
    | 'success'
    | 'info'
    | 'error'
    | 'danger'
    | 'warning';

export type NamedTheme = ThemeOptions & { name: THEME_LIST };

const SidebarWidth = 260;
const SidebarFoldedWidth = 64;
const TopbarHeight = 72;
const paddingContent = 30;
const footerHeight = 80;
const breadcrumbHeight = 70;

const baseTheme: ThemeOptions = {
    palette: {
        ...buildLightPalette(),
        contrastThreshold,
        tonalOffset,
    },
    breakpoints: {
        values: {
            xs: 0,
            sm: 620,
            md: 900,
            lg: 1150,
            xl: 1536,
        },
    },
    spacing: 8,
    shape: {
        borderRadius: 12,
    },
    mixins: {
        toolbar: {
            color: TEXT_LIGHT.SECONDARY_50,
            '@media(min-width:1280px)': {
                minHeight: TopbarHeight,
                padding: '0 30px',
            },
            '@media(max-width:1280px)': {
                minHeight: '64px',
            },
        },
    },
    components,
    typography,
    shadows,
};

const themesOptions: NamedTheme[] = [
    {
        ...baseTheme,
        name: L2D_THEME,
        palette: buildLightPalette(),
    },
    {
        ...baseTheme,
        name: BLUE_THEME,
        palette: {
            ...baseTheme.palette,
            primary: {
                main: '#1a97f5',
                light: '#e6f4ff',
                dark: '#1682d4',
            },
            secondary: {
                main: '#1e4db7',
                light: '#ddebff',
                dark: '#173f98',
            },
        },
    },
    {
        ...baseTheme,
        name: GREEN_THEME,
        palette: {
            ...baseTheme.palette,
            primary: {
                main: '#00cec3',
                light: '#d7f8f6',
                dark: '#02b3a9',
                contrastText: '#ffffff',
            },
            secondary: {
                main: '#066a73',
            },
        },
    },
    {
        ...baseTheme,
        name: PURPLE_THEME,
        palette: {
            ...baseTheme.palette,
            primary: {
                main: '#7352ff',
                light: '#e5e0fa',
                dark: '#5739d6',
            },
            secondary: {
                main: '#402e8d',
            },
        },
    },
    {
        ...baseTheme,
        name: INDIGO_THEME,
        palette: {
            ...baseTheme.palette,
            primary: {
                main: '#1e4db7',
                light: '#e6f4ff',
                dark: '#0c399e',
            },
            secondary: {
                main: '#11397b',
            },
        },
    },
    {
        ...baseTheme,
        name: ORANGE_THEME,
        palette: {
            ...baseTheme.palette,
            primary: {
                main: '#03c9d7',
                light: '#e5fafb',
                dark: '#05b2bd',
                contrastText: '#ffffff',
            },
            secondary: {
                main: '#fb9678',
                light: '#fcf1ed',
                dark: '#e67e5f',
                contrastText: '#ffffff',
            },
        },
    },
    {
        ...baseTheme,
        name: RED_THEME,
        palette: {
            ...baseTheme.palette,
            primary: {
                main: '#D32F2F',
                light: '#EF5350',
                dark: '#B71C1C',
                contrastText: '#FFFFFF',
            },
            secondary: {
                main: '#FF5722',
                light: '#FF7043',
                dark: '#BF360C',
                contrastText: '#FFFFFF',
            },
        },
    },
    {
        ...baseTheme,
        name: PINK_THEME,
        palette: {
            ...baseTheme.palette,
            primary: {
                main: '#E91E63',
                light: '#F48FB1',
                dark: '#880E4F',
                contrastText: '#FFFFFF',
            },
            secondary: {
                main: '#9C27B0',
                light: '#CE93D8',
                dark: '#6A1B9A',
                contrastText: '#FFFFFF',
            },
        },
    },
    {
        ...baseTheme,
        name: BLACK_THEME,
        palette: {
            ...baseTheme.palette,
            primary: {
                main: '#1c2025',
            },
            secondary: {
                main: '#607D8B',
            },
        },
    },
];

const themesDarkOverride: NamedTheme[] = [
    {
        ...baseTheme,
        name: L2D_THEME,
        palette: {
            ...buildDarkPalette(),
            // En dark mode, secondary.main (#0A1A53) est trop sombre sur fond sombre.
            // On permute main et light pour garder un secondary visible.
            secondary: {
                main: '#5C82FF',
                light: '#8AACFF',
                dark: '#0A1A53',
                contrastText: '#FFFFFF',
            },
        },
    },
    {
        ...baseTheme,
        name: BLUE_THEME,
        palette: {
            ...buildDarkPalette(),
            primary: {
                main: '#42A5F5',
                light: '#90CAF9',
                dark: '#1565C0',
            },
            secondary: {
                main: '#7986CB',
                light: '#9FA8DA',
                dark: '#3949AB',
            },
        },
    },
    {
        ...baseTheme,
        name: GREEN_THEME,
        palette: {
            ...buildDarkPalette(),
            primary: {
                main: '#26C6DA',
                light: '#80DEEA',
                dark: '#00838F',
            },
            secondary: {
                main: '#26A69A',
                light: '#4DB6AC',
                dark: '#00695C',
            },
        },
    },
    {
        ...baseTheme,
        name: PURPLE_THEME,
        palette: {
            ...buildDarkPalette(),
            primary: {
                main: '#9B7FFF',
                light: '#C3B1FF',
                dark: '#4527A0',
            },
            secondary: {
                main: '#9575CD',
                light: '#B39DDB',
                dark: '#512DA8',
            },
        },
    },
    {
        ...baseTheme,
        name: INDIGO_THEME,
        palette: {
            ...buildDarkPalette(),
            primary: {
                main: '#7986CB',
                light: '#9FA8DA',
                dark: '#3949AB',
            },
            secondary: {
                main: '#5C6BC0',
                light: '#7986CB',
                dark: '#283593',
            },
        },
    },
    {
        ...baseTheme,
        name: ORANGE_THEME,
        palette: {
            ...buildDarkPalette(),
            primary: {
                main: '#26C6DA',
                light: '#80DEEA',
                dark: '#0097A7',
            },
            secondary: {
                main: '#FF8A65',
                light: '#FFAB91',
                dark: '#E64A19',
            },
        },
    },
    {
        ...baseTheme,
        name: RED_THEME,
        palette: {
            ...buildDarkPalette(),
            primary: {
                main: '#EF5350',
                light: '#E57373',
                dark: '#C62828',
            },
            secondary: {
                main: '#FF7043',
                light: '#FF8A65',
                dark: '#BF360C',
            },
        },
    },
    {
        ...baseTheme,
        name: PINK_THEME,
        palette: {
            ...buildDarkPalette(),
            primary: {
                main: '#F06292',
                light: '#F48FB1',
                dark: '#C2185B',
            },
            secondary: {
                main: '#BA68C8',
                light: '#CE93D8',
                dark: '#7B1FA2',
            },
        },
    },
    {
        ...baseTheme,
        name: BLACK_THEME,
        palette: {
            ...buildDarkPalette(),
            primary: {
                main: '#607D8B',
                light: '#90A4AE',
                dark: '#37474F',
            },
            secondary: {
                main: '#90A4AE',
                light: '#607D8B',
                dark: '#37474F',
            },
        },
    },
];

export const BuildRawTheme = (
    config: {
        theme: THEME_LIST;
    } = { theme: L2D_THEME },
    override: Partial<ThemeOptions> = {},
    activeMode: CustomizerReducerActiveModeType = 'light',
): Theme => {
    let themeOptions = themesOptions.find((theme) => theme.name === config.theme);

    if (!themeOptions) {
        console.warn(new Error(`The theme ${config.theme} is not valid`));
        [themeOptions] = themesOptions;
    }

    let themeDarkOverride = {};
    if (activeMode === 'dark') {
        themeDarkOverride = themesDarkOverride.find((theme) => theme.name === config.theme) || {};
    }

    // buildDarkBase() est injecté en dernier (avant override) pour garantir que les
    // backgrounds/textes/actions sombres ne soient jamais écrasés par les valeurs
    // explicites de buildLightPalette() présentes dans baseTheme ou themeOptions.
    const darkBase = activeMode === 'dark' ? { palette: buildDarkBase() } : {};

    const theme = createTheme(
        _.merge({}, baseTheme, themeOptions, themeDarkOverride, darkBase, override),
    );

    // Second pass: inject pre-computed component tokens
    const tokens = buildComponentTokens(theme.palette);
    return createTheme(theme, {
        palette: {
            chip: tokens.chip,
            icon: tokens.icon,
            button: tokens.button,
            actionButton: tokens.actionButton,
            status: tokens.status,
        },
    });
};

export const BuildTheme = (
    config: {
        theme: THEME_LIST;
    } = { theme: L2D_THEME },
    override: Partial<ThemeOptions> = {},
): Theme => {
    const activeMode: CustomizerReducerActiveModeType = useSelector(
        (state: FlexyLayoutState) => state.customizer.activeMode,
    );
    return BuildRawTheme(config, override, activeMode);
};

export {
    TopbarHeight,
    SidebarWidth,
    SidebarFoldedWidth,
    paddingContent,
    footerHeight,
    breadcrumbHeight,
    spacing,
    contrastThreshold,
    tonalOffset,
    baseTheme,
    themesOptions,
    buildLightPalette,
    buildDarkPalette,
    buildDarkBase,
    L2D_COMPLEMENTARY,
    TEXT_LIGHT,
    TEXT_DARK,
    CARDBOARD,
    PRESCRIBER_STATUS_COLORS,
};
